// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// ui.ts — every screen, as markup. No game rules live here; it renders state and reports gestures.
//
// The dismissal contract is not optional and it is not a choice of one: every overlay ships a close
// control at least 44px square, an outside tap that dismisses, Escape, and a backdrop that is actually
// reachable. A sibling site shipped with all three handlers firing correctly and was still impossible
// to dismiss, because the `[hidden]` guard was missing and CSS pinned the panel visible.

import { BAND_COLOUR, BAND_NAME } from './palette';
import { MODES, MODE_IDS, type Mode } from './modes';
import { SLOTS } from './tuning';

export const el = <T extends HTMLElement>(sel: string, root: ParentNode = document): T =>
  root.querySelector(sel) as T;

export const FOOTER = `
  <footer class="site-footer">
    Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
    · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a>
  </footer>`;

export function shell(): string {
  return `
  <div class="main-content">
    <section id="menu" class="screen">
      <h1 class="title">Nightshift</h1>
      <p class="tag">One floor, one night, one number. Getting seen never ends the run — it changes the building.</p>
      <div class="modes" role="radiogroup" aria-label="Mode">
        ${MODE_IDS.map(
          (id, i) => `
        <button class="mode" role="radio" data-mode="${id}" aria-checked="${i === 0}">
          <span class="mode-name">${MODES[id].name}</span>
          <span class="mode-rule">${MODES[id].rule}</span>
        </button>`,
        ).join('')}
      </div>
      <p class="blurb" id="blurb">${MODES.nightshift.blurb}</p>
      <div class="menu-actions">
        <button id="play" class="primary">Break in</button>
        <button id="howto">How to play</button>
        <button id="about">About</button>
        <button id="mute" aria-pressed="false">Sound on</button>
      </div>
      <p class="best" id="best"></p>
    </section>

    <section id="game" class="screen" hidden>
      <div class="hud">
        <div class="heat">
          <div class="heat-bar"><i id="heat-fill"></i><b id="heat-floor"></b></div>
          <span id="band" class="band">CALM</span>
        </div>
        <div class="hud-right">
          <span id="turns" class="turns"></span>
          <span id="haul" class="haul"></span>
          <button id="pause" class="icon" aria-label="Pause">II</button>
        </div>
      </div>
      <div id="board-wrap" class="board-wrap"><canvas id="board" aria-label="The floor"></canvas></div>
      <p id="risk" class="risk"><span id="risk-word">CLEAR</span><span id="risk-note"></span></p>
      <div id="teach" class="teach" hidden>
        <p id="teach-text"></p>
        <div class="teach-row">
          <span id="teach-step" class="teach-step"></span>
          <button id="teach-skip" class="link">Skip</button>
        </div>
      </div>
      <div class="posture" role="radiogroup" aria-label="Posture">
        <button class="pos" data-pos="creep" role="radio" aria-checked="false">Creep</button>
        <button class="pos" data-pos="walk" role="radio" aria-checked="true">Walk</button>
        <button class="pos" data-pos="bolt" role="radio" aria-checked="false">Bolt</button>
      </div>
      <div class="actions">
        <button id="slug" class="act">Slug <b id="slug-n"></b></button>
        <button id="undo" class="act">Undo</button>
        <button id="go" class="act go">Go <b id="slots"></b></button>
      </div>
    </section>

    <section id="results" class="screen" hidden>
      <h2 id="verdict">Out</h2>
      <p id="summary" class="summary"></p>
      <div id="ledger" class="ledger"></div>
      <div class="menu-actions">
        <button id="again" class="primary">Again</button>
        <button id="share">Share this building</button>
        <button class="results-feedback">Report a problem</button>
        <button id="tomenu">Menu</button>
      </div>
    </section>
  </div>
  ${FOOTER}

  <div id="modal" class="modal" hidden>
    <div class="backdrop" id="backdrop"></div>
    <div class="sheet" role="dialog" aria-modal="true" aria-labelledby="modal-title">
      <div class="sheet-head">
        <h3 id="modal-title"></h3>
        <button id="modal-close" class="close" aria-label="Close">&times;</button>
      </div>
      <div id="modal-body" class="sheet-body"></div>
    </div>
  </div>`;
}

export const HOW_TO = `
  <p>You have five slots a turn and one floor to rob. Tap where you want to be; the route shows you what
  it costs and how exposed it leaves you, and nothing happens until you tap <b>Go</b>.</p>
  <p>Everything loud leaves a <b>mark</b> on the floor — a scuff, a door you left open, an empty case.
  A minder who walks onto a mark reads it, and that is what raises the <b>heat</b>.</p>
  <p>Heat never ends your run; it changes the building. Patrols start chasing marks, then the floor
  splits into search zones and the doors lock, then the front door seals and a custodian starts carrying
  your loot back. Slots you <i>don't</i> spend cool it down again — but only while nobody is close.</p>
  <p>Carrying more makes every step you take louder. Get out with something.</p>`;

export const ABOUT = `
  <p><b>Nightshift</b> is a turn-based stealth game about a building that reacts to you instead of a
  guard who catches you. Built by
  <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>.</p>
  <p>It runs entirely in this tab. There is no account, no server and no game data leaves your device;
  shared buildings travel inside the link itself. The only network request the page makes is an
  anonymous, cookie-less page view.</p>
  <p>Every floor is generated from a seed and then <i>verified</i> — a search that shares none of the
  generator's reasoning has to find a real route through it before you are ever shown it. That route is
  the par on the results card, so par is something somebody could actually have done.</p>`;

/** The heat bar, the band word and the ratchet notch: the whole state of the building in one strip. */
export function paintHeat(heat: number, floorHeat: number, tier: number): void {
  const fill = el<HTMLElement>('#heat-fill');
  fill.style.width = `${Math.max(0, Math.min(100, heat))}%`;
  fill.style.background = BAND_COLOUR[tier];
  const notch = el<HTMLElement>('#heat-floor');
  notch.style.left = `${Math.max(0, Math.min(100, floorHeat))}%`;
  notch.hidden = floorHeat <= 0;
  const band = el<HTMLElement>('#band');
  band.textContent = BAND_NAME[tier];
  band.style.color = BAND_COLOUR[tier];
}

export function paintTurn(turn: number, total: number, banked: number, carrying: number, slugs: number): void {
  el('#turns').textContent = `${Math.max(0, total - turn)} left`;
  el('#haul').textContent = carrying > 0 ? `${banked} banked · ${carrying} in hand` : `${banked} banked`;
  el('#slug-n').textContent = String(slugs);
}

export function paintSlots(spent: number): void {
  el('#slots').textContent = '•'.repeat(Math.max(0, SLOTS - spent));
}

/**
 * Three words, never a formula. The design printed "seen at slot 3 by minder 2", which with free
 * unlimited re-previews of a deterministic future is a solver rather than a tension device.
 */
export function paintRisk(worst: number, note: string): void {
  const word = el<HTMLElement>('#risk-word');
  word.textContent = worst >= 2 ? 'SEEN' : worst >= 1 ? 'EXPOSED' : 'CLEAR';
  word.dataset.level = String(worst);
  el('#risk-note').textContent = note;
}

export function setMode(id: string): void {
  for (const b of document.querySelectorAll<HTMLElement>('.mode')) {
    b.setAttribute('aria-checked', String(b.dataset.mode === id));
  }
  el('#blurb').textContent = MODES[id].blurb;
}

export function setPosture(id: 'creep' | 'walk' | 'bolt'): void {
  for (const b of document.querySelectorAll<HTMLElement>('.pos')) {
    b.setAttribute('aria-checked', String(b.dataset.pos === id));
  }
}

export function showScreen(which: 'menu' | 'game' | 'results'): void {
  for (const id of ['menu', 'game', 'results'] as const) el<HTMLElement>(`#${id}`).hidden = id !== which;
  // The footer's "more games" backlink is a strip of play area on a phone, and nobody explores the
  // site mid-turn. mobile.css hides it whenever body carries `playing`.
  document.body.classList.toggle('playing', which === 'game');
}

let modalOpenedAt = 0;

export function openModal(title: string, html: string): void {
  el('#modal-title').textContent = title;
  el('#modal-body').innerHTML = html;
  el<HTMLElement>('#modal').hidden = false;
  // An overlay opened from a pointerup gets the trailing click on the same gesture and closes itself
  // instantly, which reads as a dead button. Arm the outside-tap dismissal a beat late.
  modalOpenedAt = performance.now();
  el<HTMLElement>('#modal-close').focus();
}

export function closeModal(): void {
  el<HTMLElement>('#modal').hidden = true;
}

export const modalArmed = (): boolean => performance.now() - modalOpenedAt > 350;
export const modalOpen = (): boolean => !el<HTMLElement>('#modal').hidden;

export function bestLine(bests: Record<string, number>): void {
  const parts = MODE_IDS.filter((id) => bests[id] > 0).map((id) => `${MODES[id].name} ${bests[id]}`);
  el('#best').textContent = parts.length ? `Best — ${parts.join(' · ')}` : '';
}

export const modeName = (m: Mode): string => m.name;
